'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Video } from '@flipster/react/design-utils/video';
import { Styles, css } from '@flipster/styled-system/css';
import { grid, vstack } from '@flipster/styled-system/patterns';
import { ProdStaticPath } from '@flipster/utils/url';

import { authBunja } from '../../../shared/auth/state';
import { ContactChannelModal } from '../../modal/contact-channel';
import { VipButton } from '../../shared/vip-button';
import { sectionId } from '../5-apply-form';
import FlipsterVipLogo from './assets/flipster-vip.svg';
import title from './assets/title.webp';

const desktopHeroVideoSrc = `${ProdStaticPath}/videos/vip-revamp-hero-dt.mp4`;
const mobileHeroVideoSrc = `${ProdStaticPath}/videos/vip-revamp-hero-mb.mp4`;
const desktopHeroPosterSrc = `${ProdStaticPath}/images/vip-revamp-hero-dt.webp`;
const mobileHeroPosterSrc = `${ProdStaticPath}/images/vip-revamp-hero-mb.webp`;
const mobileHeroVideoAspectRatio = 1880 / 870;

interface Props {
  css?: Styles;
}

export const HeroSection = ({ css: cssProps }: Props) => {
  const overlay = useOverlay();
  const router = useRouter();
  const { isLoggedInAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  const handleTryOutVip = () => {
    if (!isLoggedIn) return router.push('/login');
    const section = document.getElementById(sectionId);
    requestAnimationFrame(() => section?.scrollIntoView({ behavior: 'smooth' }));
  };

  return (
    <section
      className={css(
        vstack.raw({
          pos: 'relative',
          gap: 40,
          tablet: {
            justifyContent: 'center',
            h: `clamp(725px, calc(100vw / ${mobileHeroVideoAspectRatio}), 1200px)`,
          },
        }),
        cssProps,
      )}
    >
      <div
        className={css({
          overflow: 'hidden',
          maxW: '100vw',
          tabletDown: {
            pos: 'relative',
            aspectRatio: mobileHeroVideoAspectRatio,
            minH: 200,
          },
          tablet: {
            pos: 'absolute',
            h: '100%',
            inset: 0,
          },
        })}
      >
        <Video
          poster={{
            base: mobileHeroPosterSrc,
            tabletToSmallDesktop: desktopHeroPosterSrc,
            smallDesktop: desktopHeroPosterSrc,
          }}
          src={{
            base: mobileHeroVideoSrc,
            tablet: desktopHeroVideoSrc,
          }}
          css={{
            w: '100%',
            h: '100%',
            objectFit: 'cover',
            objectPosition: 'center bottom',
          }}
        />
      </div>
      <div
        className={vstack({
          alignItems: 'center',
          gap: 24,
          px: 16,
          pt: { base: 24, tablet: 0 },
          zIndex: 1,
          color: 'content.default.level-5',
          textAlign: 'center',
        })}
      >
        <FlipsterVipLogo className={css({ tabletDown: { w: 140, h: 22 }, tablet: { w: 226, h: 36 } })} />
        <img src={title.src} alt='Built for Elite Trading' className={css({ maxW: 640, w: '100%', px: 16 })} />
        <p className={css({ textStyle: 'Text/L/Strong' })}>
          <FormattedMessage id='Vip.r260121.Deep_global_liquidity_Tighter_spreads_Optimized_execution' />
        </p>
      </div>
      <div
        className={grid({
          gridTemplateColumns: { base: '1fr', tablet: 'repeat(2, 1fr)' },
          gap: 10,
          tabletDown: { w: '100%' },
          zIndex: 1,
          px: 16,
          pb: { base: 24, tablet: 80 },
        })}
      >
        <VipButton
          hierarchy='primary'
          label={<FormattedMessage id='Vip.r260121.Chat_with_VIP_team_button' />}
          onClick={() => overlay.open(() => <ContactChannelModal />)}
        />
        <VipButton
          hierarchy='secondary'
          label={<FormattedMessage id='Vip.r260121.Try_out_VIP_button' />}
          onClick={handleTryOutVip}
        />
      </div>
    </section>
  );
};
